import { useEffect, useRef } from 'react'
import {
    copySelected,
    cutSelected,
    duplicateSelected,
    deleteSelected,
    pasteFromClipboard,
    selectAll,
} from '@/lib/canvasShortcuts'
import { getCanvasInstance } from '@/lib/canvasInstance'
import styles from './ContextMenu.module.css'

interface ContextMenuProps {
    x: number
    y: number
    visible: boolean
    onClose: () => void
    hasSelection: boolean
    hasClipboard: boolean
}

const MENU_WIDTH = 200
const MENU_HEIGHT_ESTIMATE = 220

export function ContextMenu({ x, y, visible, onClose, hasSelection, hasClipboard }: ContextMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null)

    // Clamp position to viewport edges
    const clampedX = Math.min(x, window.innerWidth - MENU_WIDTH - 8)
    const clampedY = Math.min(y, window.innerHeight - MENU_HEIGHT_ESTIMATE - 8)

    useEffect(() => {
        if (!visible) return

        const handleMouseDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose()
            }
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        const handleScroll = () => {
            onClose()
        }

        document.addEventListener('mousedown', handleMouseDown)
        document.addEventListener('keydown', handleKeyDown)
        window.addEventListener('scroll', handleScroll, true)

        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
            document.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('scroll', handleScroll, true)
        }
    }, [visible, onClose])

    if (!visible) return null

    const handleCopy = () => {
        copySelected()
        onClose()
    }

    const handleCut = () => {
        cutSelected()
        onClose()
    }

    const handleDuplicate = () => {
        duplicateSelected()
        onClose()
    }

    const handleBringToFront = () => {
        const canvas = getCanvasInstance()
        if (canvas) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const active = canvas.getActiveObject() as any
            if (active) {
                canvas.bringObjectToFront(active)
                canvas.requestRenderAll()
            }
        }
        onClose()
    }

    const handleSendToBack = () => {
        const canvas = getCanvasInstance()
        if (canvas) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const active = canvas.getActiveObject() as any
            if (active) {
                canvas.sendObjectToBack(active)
                canvas.requestRenderAll()
            }
        }
        onClose()
    }

    const handleDelete = () => {
        deleteSelected()
        onClose()
    }

    const handlePaste = () => {
        pasteFromClipboard()
        onClose()
    }

    const handleSelectAll = () => {
        selectAll()
        onClose()
    }

    return (
        <div
            ref={menuRef}
            className={styles.menu}
            style={{ left: clampedX, top: clampedY }}
        >
            {hasSelection ? (
                <>
                    <button className={styles.item} onClick={handleCopy}>
                        <span className={styles.itemLabel}>Copiar</span>
                        <span className={styles.itemShortcut}>Ctrl+C</span>
                    </button>
                    <button className={styles.item} onClick={handleCut}>
                        <span className={styles.itemLabel}>Cortar</span>
                        <span className={styles.itemShortcut}>Ctrl+X</span>
                    </button>
                    <button className={styles.item} onClick={handleDuplicate}>
                        <span className={styles.itemLabel}>Duplicar</span>
                        <span className={styles.itemShortcut}>Ctrl+D</span>
                    </button>
                    <div className={styles.separator} />
                    <button className={styles.item} onClick={handleBringToFront}>
                        <span className={styles.itemLabel}>Traer al frente</span>
                    </button>
                    <button className={styles.item} onClick={handleSendToBack}>
                        <span className={styles.itemLabel}>Enviar al fondo</span>
                    </button>
                    <div className={styles.separator} />
                    <button className={`${styles.item} ${styles.danger}`} onClick={handleDelete}>
                        <span className={styles.itemLabel}>Eliminar</span>
                        <span className={styles.itemShortcut}>Del</span>
                    </button>
                </>
            ) : (
                <>
                    <button
                        className={styles.item}
                        onClick={handlePaste}
                        disabled={!hasClipboard}
                    >
                        <span className={styles.itemLabel}>Pegar</span>
                        <span className={styles.itemShortcut}>Ctrl+V</span>
                    </button>
                    <button className={styles.item} onClick={handleSelectAll}>
                        <span className={styles.itemLabel}>Seleccionar todo</span>
                        <span className={styles.itemShortcut}>Ctrl+A</span>
                    </button>
                </>
            )}
        </div>
    )
}
